/* ── EU-Widerrufs-Button (§ 356a BGB) ──────────────────────────────────────
   Zweistufiges Widerrufs-Formular:
   Stufe 1 (Eingabe) → Stufe 2 (Zusammenfassung) → Bestätigen.
   Beim Bestätigen: Klaviyo-Event feuern (transaktionale Eingangsbestätigung an
   den Kunden) und das native {% form 'contact' %} regulär abschicken
   (Owner-Benachrichtigung). Kein AJAX → kein Shopify-Captcha-Konflikt.
   ─────────────────────────────────────────────────────────────────────────── */

if (!customElements.get('widerruf-form')) {
  customElements.define(
    'widerruf-form',
    class WiderrufForm extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        this.step1 = this.querySelector('[data-widerruf-step="1"]');
        this.step2 = this.querySelector('[data-widerruf-step="2"]');
        this.nextBtn = this.querySelector('[data-widerruf-next]');
        this.backBtn = this.querySelector('[data-widerruf-back]');
        this.confirmBtn = this.querySelector('[data-widerruf-confirm]');
        this.validationEl = this.querySelector('[data-widerruf-validation]');
        this.summaryHeading = this.querySelector('[data-widerruf-summary-heading]');
        this.bodyInput = this.querySelector('[data-widerruf-body]');
        this.honeypot = this.querySelector('[name="hp_company"]');

        this.nameInput = this.querySelector('[name="contact[name]"]');
        this.orderInput = this.querySelector('[name="contact[Bestellnummer]"]');
        this.emailInput = this.querySelector('[name="contact[email]"]');

        // Config aus data-Attributen
        this.minMs = (parseInt(this.dataset.minSeconds, 10) || 3) * 1000;
        this.klaviyoKey = (this.dataset.klaviyoPublicKey || '').trim();
        this.eventName = (this.dataset.klaviyoEventName || 'Widerruf eingereicht').trim();
        this.locale = this.dataset.locale || '';
        this.declarationTemplate = this.dataset.declarationTemplate || '';
        this.msgRequired = this.dataset.msgRequired || 'Bitte fülle alle Pflichtfelder aus.';
        this.msgEmail = this.dataset.msgEmail || 'Bitte gib eine gültige E-Mail-Adresse ein.';

        if (!this.form) return;

        this.currentStep = 1;
        this.loadTime = Date.now();

        // gebundene Handler (für sauberes Entfernen)
        this._onNext = this.handleNext.bind(this);
        this._onBack = this.handleBack.bind(this);
        this._onSubmit = this.handleSubmit.bind(this);
        this._onInput = this.updateFloatingLabels.bind(this);

        if (this.nextBtn) this.nextBtn.addEventListener('click', this._onNext);
        if (this.backBtn) this.backBtn.addEventListener('click', this._onBack);
        this.form.addEventListener('submit', this._onSubmit);

        this.fieldInputs = [this.nameInput, this.orderInput, this.emailInput].filter(Boolean);
        this.fieldInputs.forEach((input) => {
          input.addEventListener('input', this._onInput);
          input.addEventListener('blur', this._onInput);
        });
        this.updateFloatingLabels();
      }

      disconnectedCallback() {
        if (this.nextBtn) this.nextBtn.removeEventListener('click', this._onNext);
        if (this.backBtn) this.backBtn.removeEventListener('click', this._onBack);
        if (this.form) this.form.removeEventListener('submit', this._onSubmit);
        (this.fieldInputs || []).forEach((input) => {
          input.removeEventListener('input', this._onInput);
          input.removeEventListener('blur', this._onInput);
        });
      }

      updateFloatingLabels() {
        this.fieldInputs.forEach((input) => {
          input.classList.toggle('is-filled', input.value.trim() !== '');
        });
      }

      showValidation(message) {
        if (!this.validationEl) return;
        this.validationEl.textContent = message;
        this.validationEl.hidden = false;
      }

      clearValidation() {
        if (!this.validationEl) return;
        this.validationEl.textContent = '';
        this.validationEl.hidden = true;
      }

      validateStep1() {
        const name = this.nameInput ? this.nameInput.value.trim() : '';
        const order = this.orderInput ? this.orderInput.value.trim() : '';
        const email = this.emailInput ? this.emailInput.value.trim() : '';

        if (!name || !order || !email) {
          this.showValidation(this.msgRequired);
          return false;
        }
        // einfache, robuste E-Mail-Prüfung (HTML5 type=email als Fallback)
        const emailOk = this.emailInput && this.emailInput.checkValidity() && /.+@.+\..+/.test(email);
        if (!emailOk) {
          this.showValidation(this.msgEmail);
          if (this.emailInput) this.emailInput.focus();
          return false;
        }
        this.clearValidation();
        return true;
      }

      getValues() {
        return {
          name: this.nameInput ? this.nameInput.value.trim() : '',
          order: this.orderInput ? this.orderInput.value.trim() : '',
          email: this.emailInput ? this.emailInput.value.trim() : '',
        };
      }

      fillSummary() {
        const v = this.getValues();
        const map = { name: v.name, order: v.order, email: v.email };
        Object.keys(map).forEach((key) => {
          const cell = this.querySelector('[data-widerruf-summary="' + key + '"]');
          if (cell) cell.textContent = map[key]; // textContent → kein XSS
        });
      }

      formatNow() {
        // Explizites Format → garantiert Datum UND Uhrzeit (§ 356a: Datum + Uhrzeit des Eingangs)
        const opts = {
          year: 'numeric', month: '2-digit', day: '2-digit',
          hour: '2-digit', minute: '2-digit', second: '2-digit',
        };
        try {
          return new Date().toLocaleString(this.locale || undefined, opts);
        } catch (e) {
          return new Date().toLocaleString(undefined, opts);
        }
      }

      buildBody() {
        if (!this.bodyInput) return;
        const v = this.getValues();
        const datetime = this.formatNow();
        let body = this.declarationTemplate;
        if (body) {
          body = body
            .replace(/\{name\}/g, v.name)
            .replace(/\{order\}/g, v.order)
            .replace(/\{email\}/g, v.email)
            .replace(/\{datetime\}/g, datetime);
        } else {
          body =
            'Widerruf — Name: ' + v.name + ', Bestellnummer: ' + v.order +
            ', E-Mail: ' + v.email + ', Zeitpunkt: ' + datetime;
        }
        this.bodyInput.value = body;
      }

      goToStep(step) {
        this.currentStep = step;
        if (this.step1) this.step1.hidden = step !== 1;
        if (this.step2) this.step2.hidden = step !== 2;
      }

      handleNext() {
        if (!this.validateStep1()) return;
        this.fillSummary();
        this.buildBody();
        this.goToStep(2);
        if (this.summaryHeading) this.summaryHeading.focus();
      }

      handleBack() {
        this.goToStep(1);
        if (this.nameInput) this.nameInput.focus();
      }

      isSpam() {
        if (this.honeypot && this.honeypot.value) return true;
        if (Date.now() - this.loadTime < this.minMs) return true;
        return false;
      }

      setSubmitting() {
        if (this.confirmBtn) {
          this.confirmBtn.disabled = true;
          this.confirmBtn.classList.add('is-loading');
        }
      }

      handleSubmit(event) {
        // Enter in Stufe 1 darf nicht direkt absenden → stattdessen weiter zu Stufe 2
        if (this.currentStep !== 2) {
          event.preventDefault();
          this.handleNext();
          return;
        }

        // Spam-Schutz (Honeypot + Mindestzeit)
        if (this.isSpam()) {
          event.preventDefault();
          this.showValidation(this.msgRequired);
          return;
        }

        // Body sicherheitshalber frisch setzen
        this.buildBody();

        // Doppel-Submit sperren
        this.setSubmitting();

        // Klaviyo-Event feuern (fire-and-forget, überlebt den Page-Unload)
        this.sendKlaviyoEvent();

        // KEIN preventDefault → das native Contact-Form wird regulär abgeschickt
      }

      sendKlaviyoEvent() {
        if (!this.klaviyoKey) {
          // Kein Key konfiguriert → Owner-Mail geht trotzdem raus, aber Kunden-Bestätigung fehlt.
          console.warn('[widerruf-form] Kein Klaviyo Public Key gesetzt — keine Kunden-Bestätigung ausgelöst.');
          return;
        }
        const v = this.getValues();
        const payload = {
          data: {
            type: 'event',
            attributes: {
              metric: { data: { type: 'metric', attributes: { name: this.eventName } } },
              profile: { data: { type: 'profile', attributes: { email: v.email } } },
              properties: {
                Name: v.name,
                Bestellnummer: v.order,
                Zeitpunkt: this.formatNow(),
                locale: this.locale,
                quelle: 'Storefront Widerruf-Formular',
              },
              // 'time' bewusst weggelassen → Klaviyo stempelt den Empfangszeitpunkt (= Eingangszeitpunkt)
            },
          },
        };
        try {
          fetch(
            'https://a.klaviyo.com/client/events/?company_id=' + encodeURIComponent(this.klaviyoKey),
            {
              method: 'POST',
              mode: 'cors',
              keepalive: true,
              headers: {
                'Content-Type': 'application/json',
                revision: '2026-04-15',
              },
              body: JSON.stringify(payload),
            }
          ).catch(function (err) {
            console.warn('[widerruf-form] Klaviyo-Event fehlgeschlagen:', err);
          });
        } catch (err) {
          console.warn('[widerruf-form] Klaviyo-Event konnte nicht gesendet werden:', err);
        }
      }
    }
  );
}
